import { Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { useRef } from 'react'
import type { IconName } from '@/shared/ui/icon'
import { Icon } from '@/shared/ui/icon'
import s from './Dialog.module.css'

type DialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  icon?: IconName
  title: ReactNode
  description?: ReactNode
  width?: number
  /**
   * `false` — Escape и клик мимо окна его не закрывают: идёт шаг, который случайное закрытие
   * оборвало бы (код отсканирован, спрошен пароль). Крестик закрывает всегда — его нажимают нарочно.
   */
  dismissible?: boolean
  children: ReactNode
}

/**
 * Центрированная модалка: скрим + карточка со своей шапкой. Модель — «Новая бронь»
 * (prototype/Prototype.dc.html:2239-2316): плитка-иконка 32×32 (`--accent-soft`/`--accent`),
 * заголовок Fraunces 500/20px, подзаголовок 11.5px `--ink-3`, крестик 32×32 с hover в
 * `--st-cancel`. Это НЕ полноэкранная карточка брони со стеклянным скримом (`--glass-bg`,
 * z-index 51) — та не отсюда, она своим виджетом (`widgets/booking-modal`).
 *
 * Тело и подвал Dialog не владеет — только шапку: у модалки подключения MAX
 * (`prototype/Prototype.dc.html:1594-1624`) тот же скрим и крестик, но шапка заметно другая, а
 * общий у них ровно контур. Поэтому тело — произвольные `children`, а `width` пропс (дефолт 680),
 * чтобы модалку на 440px можно было собрать тем же компонентом.
 *
 * `open`/`onOpenChange` обязательны, не `?` — по прецеденту `Switch`: модалка без обработчика
 * значит «открылась и никогда не закроется», цена ошибки выше, чем у молча тронутого тумблера.
 *
 * ⚠️ БЕЗ `trigger`-ПРОПСА RADIX НЕ ЗНАЕТ, КУДА ВЕРНУТЬ ФОКУС: его дефолт —
 * `context.triggerRef.current?.focus()`, а `triggerRef` заполняет только `Dialog.Trigger`, которого
 * здесь нет. Проверено живьём: фокус после Escape улетал на `<body>`. Встроенный откат `FocusScope`
 * тоже не срабатывает — Radix безусловно гасит `preventDefault()`
 * (`@radix-ui/react-dialog/dist/index.mjs:148-151`). Поэтому элемент, на котором был фокус в момент
 * открытия, ловим сами — синхронно во время РЕНДЕРА, до монтирования `FocusScope`, — и возвращаем
 * своим `onCloseAutoFocus`.
 */
export function Dialog({
  open,
  onOpenChange,
  icon,
  title,
  description,
  width = 680,
  dismissible = true,
  children,
}: DialogProps) {
  const openerRef = useRef<HTMLElement | null>(null)
  // Стартуем с `false`, а НЕ с `open`: модалку можно смонтировать уже открытой (так делает
  // подтверждение действия по брони — оно и существует-то, только пока действие выбрано). При
  // старте с `open` перехода false→true не случалось никогда, кнопку-открывателя никто не
  // запоминал, и фокус после закрытия уезжал в `body` — ровно то, ради чего этот компонент и
  // заведён. У модалок, что монтируются закрытыми, поведение не меняется.
  const wasOpenRef = useRef(false)
  if (open !== wasOpenRef.current) {
    if (open) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    }
    wasOpenRef.current = open
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={s.overlay}>
          <DialogPrimitive.Content
            className={s.content}
            style={{ width: `min(${width}px, calc(100vw - 48px))` }}
            /*
             * Фокус при открытии — на САМУ панель, а не на первый tabbable.
             *
             * Дефолт Radix (`FocusScope`) фокусирует первый tabbable элемент, а первый здесь —
             * **крестик «Закрыть»**: он стоит в шапке, которую рисует сам `Dialog`. То есть человек,
             * открывший модалку с клавиатуры, получает фокус на кнопке, УНИЧТОЖАЮЩЕЙ его форму, и
             * первый же Enter стирает всё, что он набрал.
             *
             * Это не гипотеза — замерено зондом на живой сборке: `document.activeElement` после
             * открытия «Новой брони» был `BUTTON aria-label="Закрыть"`. Дефект лежал в ките с
             * задачи 5 и не был виден ровно потому, что у `Dialog` до сих пор не было НИ ОДНОГО
             * потребителя: первая же модалка его и предъявила.
             *
             * Тот же приём и по той же причине, что в `shared/ui/popover` (там фокус доставался
             * пункту «Выйти») и в `widgets/booking-modal` (там — кнопке «← Брони», закрывающей
             * карточку). Панель фокусируема: `tabIndex: -1` ставит сам Radix. Ничего не «выбрано»,
             * Tab идёт по порядку DOM, Escape и фокус-трап работают как работали.
             */
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus()
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              openerRef.current?.focus()
            }}
            onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
            onInteractOutside={dismissible ? undefined : (event) => event.preventDefault()}
          >
            <div className={s.header}>
              <div className={s.headerText}>
                {/* aria-hidden не дублируем: Icon ставит его на своём <svg> сам. */}
                {icon && (
                  <span className={s.iconTile}>
                    <Icon name={icon} size={17} strokeWidth={2.1} />
                  </span>
                )}
                <div className={s.titleGroup}>
                  <DialogPrimitive.Title className={s.title}>{title}</DialogPrimitive.Title>
                  {description && (
                    <DialogPrimitive.Description className={s.description}>
                      {description}
                    </DialogPrimitive.Description>
                  )}
                </div>
              </div>
              <DialogPrimitive.Close className={s.close} aria-label="Закрыть">
                <Icon name="close" size={15} />
              </DialogPrimitive.Close>
            </div>
            <div className={s.body}>{children}</div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
